// Hooks for the admin "Kiki Review" tab.
//
// A kiki is a user post looking for buddies — either tied to a host event or a
// free-text standalone activity. This tab is a monitoring dashboard over that
// user-generated content, with the ability to take a kiki down (soft-cancel)
// and drill into its applications + poster.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApiResponse } from "@shared/types/api";

export type KikiStatus = "active" | "fulfilled" | "cancelled";
export type KikiKind = "event" | "standalone";

export interface KikiActivityInfo {
  name: string;
  emoji?: string;
  date?: string;
  time?: string;
  location?: string;
}

export interface KikiPreferences {
  ageMin?: number;
  ageMax?: number;
  genders?: string[];
  orientations?: string[];
  interests?: string[];
}

export interface KikiSharedProfile {
  instagram?: string;
  interests?: string[];
}

export interface AdminKiki {
  id: string;
  kind: KikiKind;
  status: KikiStatus;
  activity: string | null;
  activityInfo: KikiActivityInfo | null;
  note: string | null;
  preferences: KikiPreferences | null;
  createdAt: string;
  updatedAt: string;
  posterId: string;
  posterName: string;
  posterEmail: string | null;
  posterProfileImageUrl: string | null;
  eventDetailsId: string | null;
  eventSlug: string | null;
  eventName: string | null;
  eventDate: string | null;
  eventTime: string | null;
  cityId: string | null;
  cityName: string | null;
  applicationCount: number;
  acceptedCount: number;
  pendingCount: number;
  // Moderation
  moderationScannedAt: string | null;
  flaggedAt: string | null;
  flagReason: string | null;
  flagCategories: string[] | null;
  flagReviewedAt: string | null;
  needsReview: boolean;
}

export interface AdminKikiApplication {
  id: string;
  status: string;
  note: string | null;
  sharedProfile: KikiSharedProfile | null;
  createdAt: string;
  applicantId: string;
  applicantName: string;
  applicantEmail: string | null;
  applicantProfileImageUrl: string | null;
}

export interface AdminKikiDetail extends Omit<AdminKiki, "applicationCount" | "acceptedCount" | "pendingCount"> {
  applications: AdminKikiApplication[];
  chatInFirestore: boolean;
}

export interface AdminKikisData {
  kikis: AdminKiki[];
  generatedAt: string;
  summary: {
    total: number;
    byStatus: { active: number; fulfilled: number; cancelled: number };
    byKind: { event: number; standalone: number };
    totalApplications: number;
    flaggedCount: number;
    needsReviewCount: number;
    byCity: { cityId: string; cityName: string; total: number }[];
  };
}

export interface AdminKikisFilters {
  status?: "all" | KikiStatus;
  kind?: "all" | KikiKind;
  cityId?: string;
  search?: string;
  sortBy?: "createdAt" | "applications";
  sortDir?: "asc" | "desc";
  needsReview?: boolean;
  limit?: number;
}

export function useAdminKikis(filters: AdminKikisFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== "all") params.set("status", filters.status);
  if (filters.kind && filters.kind !== "all") params.set("kind", filters.kind);
  if (filters.cityId && filters.cityId !== "all") params.set("cityId", filters.cityId);
  if (filters.search) params.set("search", filters.search);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortDir) params.set("sortDir", filters.sortDir);
  if (filters.needsReview) params.set("needsReview", "true");
  if (filters.limit) params.set("limit", String(filters.limit));
  const qs = params.toString();

  return useQuery<AdminKikisData>({
    queryKey: ["admin", "kikis", filters],
    queryFn: async () => {
      const json = await api.get<AdminKikisData>(`/admin/kikis${qs ? `?${qs}` : ""}`);
      if (!json.success || !json.data) {
        throw new Error(json.error || "Failed to load kikis");
      }
      return json.data;
    },
    staleTime: 60_000,
  });
}

export function useAdminKikiDetail(id: string | null) {
  return useQuery<AdminKikiDetail>({
    queryKey: ["admin", "kikis", "detail", id],
    enabled: !!id,
    queryFn: async () => {
      const json = await api.get<AdminKikiDetail>(`/admin/kikis/${id}`);
      if (!json.success || !json.data) {
        throw new Error(json.error || "Failed to load kiki");
      }
      return json.data;
    },
  });
}

export function useSetKikiStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: KikiStatus }): Promise<ApiResponse<{ id: string; status: KikiStatus }>> =>
      api.patch(`/admin/kikis/${id}/status`, { status }),
    onSuccess: (_res, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "kikis"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "kikis", "detail", id] });
    },
  });
}

// Count of kikis flagged by the scanner but not yet reviewed — drives the
// nav tab notification badge. Polled so the badge stays roughly live.
export interface KikiFlagCount {
  count: number;
  moderationEnabled: boolean;
}

export function useKikiFlagCount(opts: { enabled?: boolean } = {}) {
  return useQuery<KikiFlagCount>({
    queryKey: ["admin", "kikis", "flag-count"],
    enabled: opts.enabled ?? true,
    queryFn: async () => {
      const json = await api.get<KikiFlagCount>("/admin/kikis/flag-count");
      if (!json.success || !json.data) {
        throw new Error(json.error || "Failed to load flag count");
      }
      return json.data;
    },
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });
}

// Acknowledge / dismiss a moderation flag (false positive) without taking the
// kiki down. { reviewed: false } re-opens it.
export function useReviewKikiFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewed }: { id: string; reviewed: boolean }): Promise<ApiResponse<{ id: string; reviewed: boolean }>> =>
      api.patch(`/admin/kikis/${id}/review-flag`, { reviewed }),
    onSuccess: (_res, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "kikis"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "kikis", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "kikis", "flag-count"] });
    },
  });
}

export interface KikiScanResult {
  scanned: number;
  flagged: number;
  cleared: number;
  failed: number;
}

// Run the explicit-content scanner. By default only scans never-scanned kikis;
// pass { all: true } to re-scan everything.
export function useScanKikis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { all?: boolean }): Promise<ApiResponse<KikiScanResult>> =>
      api.post(`/admin/kikis/scan`, { all: opts?.all ?? false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "kikis"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "kikis", "flag-count"] });
    },
  });
}
